/* eslint-disable no-await-in-loop */
/* eslint-disable class-methods-use-this */
// eslint-disable-next-line import/no-cycle
import { apiGet, apiPost } from '@/store/api';
import { useApplicationStore } from '@/store/applicationStore';

interface IRecipient {
  NAME: string;
  EMAIL: string;
}

interface IDraftEmailFromTemplate {
  mailNo: number;
  emailId: number;
  subject: string;
  content: string;
  to: IRecipient[];
  cc: IRecipient[];
  bcc: IRecipient[];
  createdBy: string;
  location?: string;
  sednaCategoryId: number;
}

interface ISednaCategoryTag {
  id?: string;
  type?: string;
  attributes?: {
    label?: string;
    name?: string;
    archived?: boolean;
  };
}

export default class SednaApiService {
  applicationStore = useApplicationStore();

  isSednaIntegration = false;

  agencySetting;

  sednaHeaders;

  private categoryTagsCache: ISednaCategoryTag[] | null = null;

  private categoryTagsCacheTimestamp: number | null = null;

  // Cache expiration time in milliseconds (default: 2 hours)
  private readonly CACHE_EXPIRATION_TIME = 2 * 3600000;

  constructor(agencyId: number) {
    this.agencySetting = this.applicationStore.agencySettings.find((agency) => agency.ID === agencyId);
    this.isSednaIntegration = (this.agencySetting?.EMAIL_TYPE ?? 0) === 7;

    const { SEDNA_API_USR, SEDNA_API_PWD } = this.agencySetting;
    const basicAuthBase64 = btoa(`${SEDNA_API_USR}:${SEDNA_API_PWD}`);
    this.sednaHeaders = {
      accept: 'application/json',
      authorization: `Basic ${basicAuthBase64}`,
      'content-Type': 'application/vnd.api+json',
    };
  }

  private isCacheValid(): boolean {
    if (!this.categoryTagsCache || !this.categoryTagsCacheTimestamp) {
      return false;
    }

    const now = Date.now();
    return (now - this.categoryTagsCacheTimestamp) < this.CACHE_EXPIRATION_TIME;
  }

  public clearCategoryTagsCache(): void {
    this.categoryTagsCache = null;
    this.categoryTagsCacheTimestamp = null;
  }

  private async getCategoryTags(forceRefresh: boolean = false): Promise<any[]> {
    if (!forceRefresh && this.isCacheValid() && this.categoryTagsCache) {
      return this.categoryTagsCache;
    }

    const { SEDNA_URL } = this.agencySetting;
    const categoryTags: ISednaCategoryTag[] = await apiPost(`sedna/all-category-tags`, {
      SEDNA_URL,
      AUTHORIZATION: this.sednaHeaders.authorization,
    });

    this.categoryTagsCache = categoryTags;
    this.categoryTagsCacheTimestamp = Date.now();

    return categoryTags;
  }

  public async refreshCategoryTagsCache(): Promise<any[]> {
    return this.getCategoryTags(true);
  }

  public async getAllCategoryTags(): Promise<any[]> {
    return this.getCategoryTags();
  }

  fileToBase64(file: File) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async getAllSednaEmail(sednaJobId: string, onlyForCount: boolean = false) {
    // onlyForCount: Hent mail for å vise/skjule øye ikon i email tab
    const { SEDNA_URL, SEDNA_MAIL_FETCH_LIMIT, SEDNA_TEAM_ID } = this.agencySetting;
    if (SEDNA_MAIL_FETCH_LIMIT === 0) {
      return [];
    }
    const teamIdParam = SEDNA_TEAM_ID ? `&filter[team]=${SEDNA_TEAM_ID}` : '';
    const fieldsParam = onlyForCount
      ? 'fields[message]=messageId'
      : 'fields[message]=messageId,subject,hasAttachment,receivedAt,sentAt,from,to,cc,bcc';
    const apiEndPoint = `${SEDNA_URL}/job-reference/${`${sednaJobId}`.replace(
      'MIX:',
      ''
    )}/messageV2?${fieldsParam}${teamIdParam}`;
    const getMessagesResponseJsons: any[] = [];
    let cursor = null;
    const fetchLimit = onlyForCount ? 1 : SEDNA_MAIL_FETCH_LIMIT;
    if (fetchLimit === -1) {
      let hasMoreEmails = true;
      while (hasMoreEmails) {
        const cursorParam = cursor ? `&page[cursor]=${cursor}` : '';
        const linkToFetch = `${apiEndPoint}${cursorParam}`;
        const responseJson = await apiPost(`sedna/mail/read`, {
          SEDNA_URL: linkToFetch,
          AUTHORIZATION: this.sednaHeaders.authorization,
        });
        cursor = responseJson?.nextPageCursor;
        const hasEmails = responseJson?.response?.data?.length > 0;
        hasMoreEmails = hasEmails && cursor;
        if (hasEmails) {
          getMessagesResponseJsons.push(responseJson.response);
        }
      }
    } else {
      let limit = fetchLimit;
      let emailToFetch = Math.max(0, Math.min(limit, 100));
      while (emailToFetch > 0) {
        const cursorParam = cursor ? `&page[cursor]=${cursor}` : '';
        const newLinkToFetch = `${apiEndPoint}${cursorParam}&page[limit]=${emailToFetch}`;
        const responseJson = await apiPost(`sedna/mail/read`, {
          SEDNA_URL: newLinkToFetch,
          AUTHORIZATION: this.sednaHeaders.authorization,
        });
        const currentCount = responseJson?.response?.data?.length ?? 0;
        if (currentCount > 0) {
          limit -= emailToFetch;
          emailToFetch = responseJson.nextPageCursor ? 0 : Math.min(limit, 100);
          cursor = responseJson.nextPageCursor;
          getMessagesResponseJsons.push(responseJson.response);
        } else {
          emailToFetch = 0;
        }
      }
    }

    if (onlyForCount) {
      return getMessagesResponseJsons;
    }

    const categoryTags = await this.getCategoryTags();

    getMessagesResponseJsons.forEach(response => {
      response.data.forEach((eml: any) => {
        eml.relationships['category-tag']?.data.forEach((tag: any) => {
          // eslint-disable-next-line no-param-reassign
          tag.label = categoryTags.find((cat: any) => cat.id === tag.id)?.attributes?.label || null;
        })
      })
    })

    return getMessagesResponseJsons;
  }

  async getSednaJobId(locationId: number, location: string) {
    const sednaJobId = await apiGet(`sedna/sednajobid/${location}/${locationId}`);
    return sednaJobId;
  }

  async createDraftMessage(sednaDraftMessage: any) {
    const { SEDNA_URL } = this.agencySetting;
    const responseJson = await apiPost(`sedna/mail/write/draft`, {
      SEDNA_URL: `${SEDNA_URL}`,
      AUTHORIZATION: this.sednaHeaders.authorization,
      PAYLOAD: JSON.stringify(sednaDraftMessage),
    });
    return responseJson;
  }

  async updateDraftMessageWithJobId(sednaJobId: string, sednaMailId: number) {
    const { SEDNA_URL } = this.agencySetting;

    // update the draft with sedna job id
    const success = await apiPost(`sedna/mail/update`, {
      SEDNA_URL: `${SEDNA_URL}/message/${sednaMailId}/relationships/job-reference`,
      AUTHORIZATION: this.sednaHeaders.authorization,
      PAYLOAD: JSON.stringify({ data: [{ id: `${sednaJobId}`.replace('MIX:', '') }] }),
    });
    return success;
  }

  async addMessageCategoryRelationship(sednaUrl: string, payload: string) {
    // validate sedna category tag id
    const success = await apiPost(`sedna/category-tag`, {
      SEDNA_URL: sednaUrl,
      AUTHORIZATION: this.sednaHeaders.authorization,
      PAYLOAD: payload,
    });

    return success;
  }

  async setMessageCategoryRelationship(categoryTagId: number, sednaMailId: number) {
    const { SEDNA_URL } = this.agencySetting;

    const sednaUrl = `${SEDNA_URL}/category-tag/${categoryTagId}/relationships/message`;
    const payload = JSON.stringify({ data: [{ id: `${sednaMailId}` }] });
    const success = await this.addMessageCategoryRelationship(sednaUrl, payload);

    return success;
  }

  async setCategoryMessageRelationship(categoryTagId: number, sednaMailId: number) {
    const { SEDNA_URL } = this.agencySetting;

    const sednaUrl = `${SEDNA_URL}/message/${sednaMailId}/relationships/category-tag`;
    const payload = JSON.stringify({ data: [{ id: `${categoryTagId}` }] });
    const success = await this.addMessageCategoryRelationship(sednaUrl, payload);

    return success;
  }

  async uploadAttachments(sednaMailId: number, attachments: File[]) {
    const { SEDNA_URL } = this.agencySetting;
    attachments.forEach(async (file) => {
      const fileAsBase64 = await this.fileToBase64(file);
      const attachment = { base64Content: fileAsBase64, fileName: file.name, fileSize: file.size };
      await apiPost(`sedna/mail/update`, {
        SEDNA_URL: `${SEDNA_URL}/message/${sednaMailId}/document`,
        AUTHORIZATION: this.sednaHeaders.authorization,
        PAYLOAD: JSON.stringify({ data: attachment }),
      });
    });
  }

  createComposeEditSednaEmailUrl(sednaMailId: number) {
    const { SEDNA_URL, SEDNA_TEAM_ID } = this.agencySetting;
    const urlParts = SEDNA_URL.split('.com');
    const teamPart = SEDNA_TEAM_ID ? `?teamId=${SEDNA_TEAM_ID}` : '';
    const externalSednaUrl = `${urlParts[0]}.com/compose/message/${sednaMailId ?? 'new'}${teamPart}`;
    return externalSednaUrl;
  }

  createOpenSednaEmailUrl(sednaMailId: number) {
    const { SEDNA_URL, SEDNA_TEAM_ID } = this.agencySetting;
    const urlParts = SEDNA_URL.split('.com');
    const teamPart = SEDNA_TEAM_ID ? `?teamId=${SEDNA_TEAM_ID}` : '';
    const externalSednaUrl = `${urlParts[0]}.com/list/message/${sednaMailId}${teamPart}`;
    return externalSednaUrl;
  }

  createReplySednaEmailUrl(sednaMailId: number, replyType: null | 'all' | 'forward' | 'open') {
    if (replyType === 'open') {
      return this.createOpenSednaEmailUrl(sednaMailId);
    }
    const { SEDNA_URL, SEDNA_TEAM_ID } = this.agencySetting;
    const urlParts = SEDNA_URL.split('.com');
    const teamPart = SEDNA_TEAM_ID ? `?teamId=${SEDNA_TEAM_ID}` : '';
    const firstPart = `${urlParts[0]}.com/follow-up/message/${sednaMailId}/`;
    // eslint-disable-next-line no-nested-ternary
    const middlePart = replyType === 'all' ? 'replyAll' : replyType === 'forward' ? 'forward' : 'reply';
    const externalSednaUrl = `${firstPart}${middlePart}${teamPart}`;
    return externalSednaUrl;
  }

  async openSednaUrlForJobTag(
    sednaJobId: string,
    allTeams: boolean,
    mailboxType: 'all' | 'draft' | 'inbox' | 'myWork' | 'following' | 'sent' = 'all'
  ) {
    const { SEDNA_URL, SEDNA_TEAM_ID } = this.agencySetting;
    const urlParts = SEDNA_URL.split('.com');

    const mailBoxToFilter = `&filter=${mailboxType}`;
    const teamFilter = SEDNA_TEAM_ID ? `&teamId=${SEDNA_TEAM_ID}` : '';
    const teamsFilter = allTeams ? '&teams=all' : '';
    const responseJson = await apiPost(`sedna/mail/read`, {
      SEDNA_URL: `${SEDNA_URL}/job-reference/${`${sednaJobId}`.replace('MIX:', '')}`,
      AUTHORIZATION: this.sednaHeaders.authorization,
    });
    if (responseJson?.data?.attributes) {
      const urlParams = {
        type: 'and',
        children: [
          {
            term: {
              type: 'jobReference',
              query: responseJson.data.attributes.name,
              _data: { name: responseJson.data.attributes.name, tagId: responseJson.data.attributes.tagId },
            },
          },
        ],
      };
      const firstPart = `${urlParts[0]}.com/list?filterSet=`;
      const middlePart = encodeURI(JSON.stringify(urlParams));
      const lastPart = `${mailBoxToFilter}${teamFilter}${teamsFilter}`;
      const externalSednaUrl = `${firstPart}${middlePart}${lastPart}`;
      return externalSednaUrl;
    }
    return null;
  }

  async createSednaJobRef(locationId: number, location: string) {
    // create job reference and job id in gs-backend
    const { SEDNA_URL } = this.agencySetting;
    const sednaPostResponseJson = await apiPost(`sedna/sednajobref/create/${location}/${locationId}`, {
      SEDNA_URL: `${SEDNA_URL}`,
      AUTHORIZATION: this.sednaHeaders.authorization,
    });

    return sednaPostResponseJson?.payload; // sedna job id
  }

  async updateSednaJobIdInSedna(location: string, locationId: number, sednaJobId: string, followers: string[]) {
    const { SEDNA_URL } = this.agencySetting;
    const gsResponse = await apiPost(`sedna/sednajobref/${location}/${locationId}`, {
      SEDNA_URL: `${SEDNA_URL}`,
      AUTHORIZATION: this.sednaHeaders.authorization,
      SEDNA_JOB_ID: sednaJobId,
      FOLLOWERS: followers,
    });

    // update job id in sedna
    const success = await apiPost(`sedna/mail/patch`, {
      SEDNA_URL: `${SEDNA_URL}/job-reference/${`${sednaJobId}`.replace('MIX:', '')}`,
      AUTHORIZATION: this.sednaHeaders.authorization,
      PAYLOAD: JSON.stringify(gsResponse),
    });

    return success;
  }

  splitMultipleReceipients(email: string, name: string) {
    const emails = email.replace(/\s/g, '').split(/[,;]/g);
    if (emails.length > 1) {
      return emails.map((eml) => ({ email: eml }));
    }
    return emails.map((eml) => ({ email: eml, name }));
  }

  openNewSednaEmailUrl(sednaResponse: { error?: string; url?: string }) {
    if (sednaResponse.url) {
      window.open(sednaResponse.url, '_blank');
    } else if (sednaResponse.error) {
      window.messageBus.$emit('showMessage', {
        type: 'error',
        text: 'Error sending sedna email',
        subText: `${sednaResponse.error}` || '',
        icon: true,
      });
    }
  }

  async composeNewSednaEmail(sednaJobId: string, attachments: File[], draftMessage?: IDraftEmailFromTemplate) {
    const { SEDNA_TEAM_ID } = this.agencySetting;
    const fromUser = this.applicationStore.user.sednaUserId?.length
      ? this.applicationStore.user.sednaUserId
      : this.applicationStore.user.userRights['U.EMAIL'];
    const fromTeam = SEDNA_TEAM_ID?.length ? SEDNA_TEAM_ID : '';
    const categoryId = draftMessage?.sednaCategoryId ?? null;

    const sednaDraftMessage = {
      data: {
        type: 'string',
        attributes: {
          fromUser,
          fromTeam,
          body: draftMessage?.content ?? '',
          subject: draftMessage?.subject ?? '',
          to: draftMessage?.to?.map((t) => this.splitMultipleReceipients(t.EMAIL, t.NAME))?.flat() ?? [],
          cc: draftMessage?.cc?.map((t) => this.splitMultipleReceipients(t.EMAIL, t.NAME))?.flat() ?? [],
          bcc: draftMessage?.bcc?.map((t) => this.splitMultipleReceipients(t.EMAIL, t.NAME))?.flat() ?? [],
        },
      },
    };

    // create a draft message
    const responseJson = await this.createDraftMessage(sednaDraftMessage);
    const sednaMailId = responseJson?.data?.id;

    if (sednaMailId) {
      // update the draft with sedna job id
      const updateJobIdStatus = await this.updateDraftMessageWithJobId(sednaJobId, sednaMailId);
      if (!updateJobIdStatus) {
        return {
          error: `Could not update the sedna draft email with job id (job id: ${sednaJobId}, mail id: ${sednaMailId})`,
        };
      }

      // validate and update the draft with sedna category id
      if (categoryId) {
        const messageCategoryResult = await this.setMessageCategoryRelationship(categoryId, sednaMailId);
        if (messageCategoryResult) {
          const categoryMessageResult = await this.setCategoryMessageRelationship(categoryId, sednaMailId);
          if (!categoryMessageResult) {
            return {
              error: `Could not update the sedna draft email with category id (category id: ${categoryId}, mail id: ${sednaMailId})`,
            };
          }
        }
      }

      // attach the documents to send
      if (attachments.length > 0) {
        await this.uploadAttachments(sednaMailId, attachments);
      }

      // open sedna web editor or gatship web email editor
      const externalSednaUrl = this.createComposeEditSednaEmailUrl(sednaMailId);
      // eslint-disable-next-line consistent-return
      return {
        url: externalSednaUrl,
      };
    }

    return {
      error:
        responseJson?.errors?.length > 0
          ? responseJson.errors
            .map(
              (err: { code?: string; detail?: string; status?: number }) =>
                `${err?.code} (${err?.status}): ${err?.detail}`
            )
            .join('\r\n')
          : 'Could not create a draft email in sedna',
    };
  }
}
